import React from "react";
import { useTranslation } from "react-i18next";
import { IconDotsVertical } from "@tabler/icons-react";
import { LangConstant } from "const";
import { Pagination } from "components";
import UserActiveFunction from "./UserActiveFunction";
import { useRole } from "hooks";
import UserStatusJoin from "./UserStatusJoin";

const UserListTable = ({
  isLoading,
  users,
  pagination,
  checkedUsers,
  onCheckBoxChange,
  onCheckAllChange,
  onClearAllChecked,
  onClickAction,
  onChangePage,
}) => {
  const { t: getLabel } = useTranslation();
  const { isServiceAdmin } = useRole();

  const handleCheckboxChange = (event, user) => {
    onCheckBoxChange(event.target.checked, user);
  };

  const isAllChecked =
    users.length > 0 && users.every(user => checkedUsers.some(u => u.id === user.id));

  const handleToggleCheckAll = event => {
    if (onCheckAllChange) {
      onCheckAllChange(event.target.checked);
    }
  };

  const totalPages = pagination.total;
  const currentPage = pagination.current;
  const selectedCount = checkedUsers.length;

  // Số cột để căn colSpan cho hàng loading/empty:
  // service admin: checkbox + name + phone + unit + dept + position + password + action = 8
  // admin thường: checkbox + name + unit + dept + position + "Trạng thái hoạt động" + action = 7
  const columnCount = isServiceAdmin ? 8 : 7;

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
        <span style={{ fontSize: "larger", fontWeight: "bold" }}>
          {getLabel(LangConstant.TXT_TOTAL_USERS_COUNT, { count: pagination.total_users })}
        </span>
        {selectedCount > 0 && (
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-blue-lt">
              Đã chọn {selectedCount}
            </span>
            <button
              onClick={onClearAllChecked}
              className="btn btn-ghost-secondary btn-sm text-nowrap p-0 text-decoration-none"
              style={{ fontSize: "0.875rem" }}
              title="Bỏ chọn tất cả người dùng"
            >
              (Bỏ chọn tất cả)
            </button>
          </div>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className="table table-vcenter">
          <thead>
            <tr>
              <th style={{ width: "5%" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isAllChecked}
                  onChange={handleToggleCheckAll}
                  title={isAllChecked ? "Bỏ chọn tất cả trang này" : "Chọn tất cả trang này"}
                />
              </th>
              <th className="text-nowrap" style={{ width: "25%" }}>
                <strong>{isServiceAdmin ? getLabel("TXT_FULL_NAME") : getLabel("TXT_USER")}</strong>
              </th>
              {isServiceAdmin ? (
                <>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_PHONENUMBER")}</strong></th>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_UNIT")}</strong></th>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_DEPARTMENT")}</strong></th>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_POSITION")}</strong></th>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_PASSWORD")}</strong></th>
                </>
              ) : (
                <>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_UNIT")}</strong></th>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_DEPARTMENT")}</strong></th>
                  <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel("TXT_POSITION")}</strong></th>
                </>
              )}
              {!isServiceAdmin && (
                <th className="text-nowrap" style={{ width: "13%" }}><strong>{getLabel(LangConstant.L_ACTIVE_FUNCTION)}</strong></th>
              )}
              <th className="text-nowrap" style={{ width: "5%" }}></th>
            </tr>
          </thead>
          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={columnCount} className="text-center">
                  <small>{getLabel(LangConstant.TXT_LOADING)}</small>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {users.length > 0 ? (
                users.map((user, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        onChange={event => handleCheckboxChange(event, user)}
                        checked={checkedUsers.map(u => u.id).includes(user.id)}
                      />
                    </td>
                    <td>
                      <span style={{ fontSize: "14px" }}>
                        {user.name} {isServiceAdmin ? "" : `(${user.phone})`}
                      </span>
                    </td>
                    {isServiceAdmin ? (
                      <>
                        <td>{user.phone || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                        <td>{user.unitName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                        <td>{user.departmentName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                        <td>{user.positionName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                        <td>{user.pin || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                      </>
                    ) : (
                      <>
                        <td>{user.unitName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                        <td>{user.departmentName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                        <td>{user.positionName || getLabel(LangConstant.TXT_NOT_FOUND)}</td>
                      </>
                    )}
                    {!isServiceAdmin && (
                      <td>
                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
                          <UserActiveFunction user={user} />
                          <UserStatusJoin user={user} />
                        </div>
                      </td>
                    )}
                    <td>
                      <button className="btn btn-icon btn-ghost-secondary" onClick={event => onClickAction(event, user)}>
                        <IconDotsVertical size={20} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columnCount} className="text-center">
                    <small>{getLabel(LangConstant.TXT_NOT_FOUND)}</small>
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onChangePage} />
    </div>
  );
};

export default UserListTable;
